import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ToastProvider } from './context/ToastContext';
import { OrderUpdatesProvider } from './context/OrderUpdatesContext';
import { StoreStatusProvider } from './context/StoreStatusContext';
import { FirebaseAnalyticsProvider } from './context/FirebaseContext';
import { LoggingProvider } from './context/LoggingContext';
import RouteTracker from './components/analytics/RouterTracker';
import ScrollToTop from './components/layout/ScrollToTop';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Loading from './components/ui/Loading';
// Home stays eager since it's the landing page most visitors hit first.
import Home from './pages/Home';
import './styles/index.css';
import AnnouncementManager from './components/announcement/AnnouncementManager';
import UpdateAvailableBanner from './components/pwa/UpdateAvailableBanner';
import EnableNotificationsBanner from './components/pwa/EnableNotificationsBanner';
import NotificationForegroundListener from './components/pwa/NotificationForegroundListener';

// Everything else (including the whole admin panel) is code-split so a
// storefront visitor never downloads admin-only JS.
const Stores = lazy(() => import('./pages/Stores'));
const Search = lazy(() => import('./pages/Search'));
const Store = lazy(() => import('./pages/Store'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Profile = lazy(() => import('./pages/Profile'));
const OrderHistory = lazy(() => import('./pages/OrderHistory'));
const AdminApp = lazy(() => import('./admin/AdminApp'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <Loading size="large" />
  </div>
);

const FullScreenLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loading size="large" />
  </div>
);

function App() {
  return (
    <Router>
      <UpdateAvailableBanner />
      <FirebaseAnalyticsProvider>
        <Routes>
          {/* Admin Routes */}
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <AdminApp />
              </Suspense>
            }
          />

          {/* Client Routes */}
          <Route
            path="/*"
            element={
              <AuthProvider>
                <LoggingProvider>
                  <CartProvider>
                    <ToastProvider>
                      <OrderUpdatesProvider>
                        <StoreStatusProvider>
                          <div className="min-h-screen flex flex-col">
                            <RouteTracker />
                            <ScrollToTop />
                            <NotificationForegroundListener />
                            <Header />
                            <AnnouncementManager />
                            <EnableNotificationsBanner />
                            <main className="flex-grow">
                              <Suspense fallback={<PageLoader />}>
                                <Routes>
                                  <Route path="/" element={<Home />} />
                                  <Route path="/stores" element={<Stores />} />
                                  <Route path="/search" element={<Search />} />
                                  <Route path="/store/:storeId" element={<Store />} />
                                  <Route path="/about" element={<About />} />
                                  <Route path="/contact" element={<Contact />} />
                                  <Route path="/privacy" element={<Privacy />} />
                                  <Route path="/terms" element={<Terms />} />
                                  <Route path="/checkout" element={<Checkout />} />
                                  <Route path="/profile" element={<Profile />} />
                                  <Route path="/order-history" element={<OrderHistory />} />
                                  <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                              </Suspense>
                            </main>
                            <Footer />
                          </div>
                        </StoreStatusProvider>
                      </OrderUpdatesProvider>
                    </ToastProvider>
                  </CartProvider>
                </LoggingProvider>
              </AuthProvider>
            }
          />
        </Routes>
      </FirebaseAnalyticsProvider>
    </Router>
  );
}

export default App;